import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { OPEN_ORDERS } from '../../infrastructure/config/api';

const OpenOrdersPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editQty, setEditQty] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchOrders = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    setIsRefreshing(true);
    try {
      const response = await api.get(OPEN_ORDERS.LIST);
      setOrders(response.data.data || []);
    } catch (error) {
      enqueueSnackbar('Failed to load open orders', { variant: 'error' });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleCancel = async (order) => {
    try {
      await api.post(OPEN_ORDERS.CANCEL, { symbol: order.symbol, orderId: order.orderId });
      enqueueSnackbar(`Canceled ${order.symbol}`, { variant: 'success' });
      await fetchOrders(false);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Cancel failed', { variant: 'error' });
    }
  };

  const handleOpenEdit = (order) => {
    setEditOrder(order);
    setEditPrice(order.price || order.stopPrice || '');
    setEditQty(order.origQty || '');
  };

  const handleCloseEdit = () => {
    setEditOrder(null);
    setEditPrice('');
    setEditQty('');
  };

  const handleUpdate = async () => {
    if (!editOrder) return;
    setIsUpdating(true);
    try {
      await api.post(OPEN_ORDERS.REPLACE, {
        symbol: editOrder.symbol,
        orderId: editOrder.orderId,
        price: editPrice,
        quantity: editQty,
        side: editOrder.side
      });
      enqueueSnackbar(`Updated ${editOrder.symbol}`, { variant: 'success' });
      handleCloseEdit();
      await fetchOrders(false);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || 'Update failed', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Open Orders</Typography>
        <Button variant="outlined" onClick={() => fetchOrders(false)} disabled={isRefreshing}>
          {isRefreshing ? 'Refreshing...' : 'Scan'}
        </Button>
      </Box>

      <Paper sx={{ p: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Side</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Price</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Qty</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Status</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Account</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order, index) => (
              <TableRow key={`${order.symbol}-${order.orderId}`}> 
                <TableCell>{index + 1}</TableCell>
                <TableCell>{order.symbol}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={order.side}
                    color={order.side === 'BUY' ? 'success' : 'error'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{order.type}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {order.price || order.stopPrice || '-'}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {order.origQty}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {order.status}
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  {order.accountIndex}
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenEdit(order)}
                      disabled={order.type !== 'LIMIT'}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => handleCancel(order)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>No open orders</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!editOrder} onClose={handleCloseEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {editOrder?.symbol} ({editOrder?.side})
          </Typography>
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            margin="dense"
          />
          <TextField
            fullWidth
            label="Quantity"
            type="number"
            value={editQty}
            onChange={(e) => setEditQty(e.target.value)}
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Close</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OpenOrdersPage;
